import { GuestMeetingEntry } from "@shared/utils";
import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";

/**
 * Interface for meeting details fetched from server
 */
export interface PreJoinMeetingDetails {
  meetingType?: number;
  scheduleStartDate?: string;
  scheduleEndDate?: string;
  startTime?: string;
  endTime?: string;
  title?: string;
}

/**
 * PreJoin Slice - Manages state for the pre-join/waiting room screen
 */
export interface PreJoinState {
  // User info
  username: string;
  guestEmail: string;
  isGuestMode: boolean;
  guestData: GuestMeetingEntry | null;

  // Join status
  isJoining: boolean;
  isAutoJoining: boolean;
  isRejected: boolean;
  rejectedMessage: string | null;

  // Meeting info
  meetingDetails: PreJoinMeetingDetails | null;

  // Media settings
  mediaDefaults: {
    audioEnabled: boolean;
    videoEnabled: boolean;
  };

  // Selected devices (persisted in redux to share between components if needed, though mostly handled by hooks)
  selectedAudioDeviceId: string | undefined;
  selectedVideoDeviceId: string | undefined;
}

const initialState: PreJoinState = {
  username: "",
  guestEmail: "",
  isGuestMode: false,
  guestData: null,
  isJoining: false,
  isAutoJoining: false,
  isRejected: false,
  rejectedMessage: null,
  meetingDetails: null,
  mediaDefaults: {
    audioEnabled: false,
    videoEnabled: false
  },
  selectedAudioDeviceId: undefined,
  selectedVideoDeviceId: undefined
};

const preJoinSlice = createSlice({
  name: "preJoin",
  initialState,
  reducers: {
    setUsername(state, action: PayloadAction<string>) {
      state.username = action.payload;
    },
    setGuestEmail(state, action: PayloadAction<string>) {
      state.guestEmail = action.payload;
    },
    setGuestMode(state, action: PayloadAction<boolean>) {
      state.isGuestMode = action.payload;
    },
    setGuestData(state, action: PayloadAction<GuestMeetingEntry | null>) {
      state.guestData = action.payload;
      // If we have guest data, sync the username
      if (action.payload?.guestName) {
        state.username = action.payload.guestName;
      }
      if (action.payload?.guestEmail) {
        state.guestEmail = action.payload.guestEmail;
      }
    },
    setIsJoining(state, action: PayloadAction<boolean>) {
      state.isJoining = action.payload;
    },
    setIsAutoJoining(state, action: PayloadAction<boolean>) {
      state.isAutoJoining = action.payload;
    },
    setJoinRejection(state, action: PayloadAction<{ isRejected: boolean; message?: string | null }>) {
      state.isRejected = action.payload.isRejected;
      state.rejectedMessage = action.payload.message || null;
    },
    setMeetingDetails(state, action: PayloadAction<PreJoinMeetingDetails | null>) {
      state.meetingDetails = action.payload;
    },
    setMediaDefaults(state, action: PayloadAction<{ audioEnabled: boolean; videoEnabled: boolean }>) {
      state.mediaDefaults = action.payload;
    },
    setSelectedDevice(state, action: PayloadAction<{ kind: "audio" | "video"; deviceId: string }>) {
      if (action.payload.kind === "audio") {
        state.selectedAudioDeviceId = action.payload.deviceId;
      } else {
        state.selectedVideoDeviceId = action.payload.deviceId;
      }
    },
    resetPreJoinState() {
      return initialState;
    }
  }
});

export const {
  setUsername,
  setGuestEmail,
  setGuestMode,
  setGuestData,
  setIsJoining,
  setIsAutoJoining,
  setJoinRejection,
  setMeetingDetails,
  setMediaDefaults,
  setSelectedDevice,
  resetPreJoinState
} = preJoinSlice.actions;

// Selectors
export const selectPreJoinState = (state: { preJoin: PreJoinState }) => state.preJoin;
export const selectUsername = (state: { preJoin: PreJoinState }) => state.preJoin.username;
export const selectGuestEmail = (state: { preJoin: PreJoinState }) => state.preJoin.guestEmail;
export const selectIsJoining = (state: { preJoin: PreJoinState }) => state.preJoin.isJoining;
export const selectIsGuestMode = (state: { preJoin: PreJoinState }) => state.preJoin.isGuestMode;
export const selectGuestData = (state: { preJoin: PreJoinState }) => state.preJoin.guestData;
export const selectJoinRejection = createSelector(
  [
    (state: { preJoin: PreJoinState }) => state.preJoin.isRejected,
    (state: { preJoin: PreJoinState }) => state.preJoin.rejectedMessage
  ],
  (isRejected, rejectedMessage) => ({
    isRejected,
    rejectedMessage
  })
);
export const selectPreJoinMeetingDetails = (state: { preJoin: PreJoinState }) => state.preJoin.meetingDetails;
export const selectMediaDefaults = (state: { preJoin: PreJoinState }) => state.preJoin.mediaDefaults;
export const selectIsAutoJoining = (state: { preJoin: PreJoinState }) => state.preJoin.isAutoJoining;

// Derived selectors
export const selectIsNameEditable = (state: { preJoin: PreJoinState }) =>
  // Guests must be able to enter or correct their display name on every device.
  // Existing guest data is only a prefill and should not make the input read-only.
  state.preJoin.isGuestMode;
export const selectIsEmailEditable = (state: { preJoin: PreJoinState }) =>
  state.preJoin.isGuestMode && !state.preJoin.guestData?.guestEmail;

export default preJoinSlice.reducer;
